import type {
  AgentWorkspaceModelStatus,
  AgentWorkspaceOntologyGeneratedApplicationBridge,
} from "./agent-workspace-model";
import { readProofField, readRecord } from "./source-to-application-quality";

export const ONTOLOGY_GENERATED_APPLICATION_STUDIO_INTERFACE_SCHEMA_VERSION =
  "cogo.ontology-generated-application-studio-interface.v1" as const;

export const AGENT_WORKSPACE_ONTOLOGY_GENERATED_APPLICATION_BRIDGE_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-ontology-generated-application-bridge.v1" as const;

export interface OntologyGeneratedApplicationStudioInterface {
  schemaVersion: typeof ONTOLOGY_GENERATED_APPLICATION_STUDIO_INTERFACE_SCHEMA_VERSION;
  generatedAt?: string;
  status: string;
  artifact?: {
    appId?: string;
    title?: string;
    artifactKind?: string;
    generationMode?: string;
    screenCount?: number;
    componentCount?: number;
    actionFlowCount?: number;
  };
  sourceAuthority?: {
    finalArtifactWriter?: string;
    promotionAuthority?: string;
    modelRoles?: Record<string, string>;
    deterministicCompilerHash?: string;
    sourcePromotionRef?: string;
    modelOrchestrationRef?: string;
  };
  interfaceRefs?: {
    applicationJson?: string;
    previewHtml?: string;
    generationProof?: string;
    sourcePromotion?: string;
    modelOrchestration?: string;
    screenshots?: string[];
  };
  studioSurfaces?: Array<{
    id?: string;
    surfaceKind?: string;
    reads?: string[];
    purpose?: string;
  }>;
  studioFlow?: {
    states?: Array<{
      id?: string;
      owner?: string;
      description?: string;
    }>;
    transitions?: Array<{
      id?: string;
      from?: string;
      to?: string;
      trigger?: string;
      guard?: string;
    }>;
  };
  studioActions?: Array<{
    id?: string;
    actor?: string;
    actionKind?: string;
    reads?: string[];
    writes?: string[];
  }>;
  validationContract?: {
    requiredChecks?: string[];
    blockedClaims?: string[];
    minimumAcceptStatus?: string;
  };
  checks?: Array<{
    id?: string;
    status?: string;
    observed?: string;
    expected?: string;
    gaps?: string[];
  }>;
  claimBoundary?: string;
}

function readString(value: unknown, fallback = "unknown"): string {
  return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function readModelRoles(value: unknown): Record<string, string> {
  const record = readRecord(value);
  if (!record) return {};
  return Object.fromEntries(
    Object.entries(record).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

function statusToAgentStatus(status: unknown): AgentWorkspaceModelStatus {
  const normalized = readString(status, "missing").toLowerCase();
  if (normalized === "pass" || normalized === "ready" || normalized === "passed") {
    return "ready";
  }
  if (normalized === "fail" || normalized === "failed" || normalized === "blocked") {
    return "blocked";
  }
  if (normalized === "missing") return "missing";
  return "warn";
}

function countPassingChecks(
  checks: OntologyGeneratedApplicationStudioInterface["checks"],
): number {
  return (checks ?? []).filter((check) =>
    ["pass", "ready", "passed"].includes(readString(check.status, "").toLowerCase()),
  ).length;
}

export function readProjectOntologyGeneratedApplicationInterface(
  project: unknown,
): OntologyGeneratedApplicationStudioInterface | null {
  const candidate =
    readRecord(readProofField(project, "ontologyGeneratedApplicationStudioInterface")) ??
    readRecord(readProofField(project, "ontologyGeneratedApplicationInterface")) ??
    readRecord(readProofField(project, "studioOntologyGeneratedApplicationInterface"));
  if (!candidate) return null;
  if (
    candidate.schemaVersion !==
    ONTOLOGY_GENERATED_APPLICATION_STUDIO_INTERFACE_SCHEMA_VERSION
  ) {
    return null;
  }
  return candidate as unknown as OntologyGeneratedApplicationStudioInterface;
}

export function buildAgentWorkspaceOntologyGeneratedApplicationBridge(
  studioInterface: OntologyGeneratedApplicationStudioInterface,
): AgentWorkspaceOntologyGeneratedApplicationBridge {
  const status = statusToAgentStatus(studioInterface.status);
  const artifact = studioInterface.artifact ?? {};
  const sourceAuthority = studioInterface.sourceAuthority ?? {};
  const interfaceRefs = studioInterface.interfaceRefs ?? {};
  const studioSurfaces = studioInterface.studioSurfaces ?? [];
  const studioStates = studioInterface.studioFlow?.states ?? [];
  const studioActions = studioInterface.studioActions ?? [];
  const requiredChecks = studioInterface.validationContract?.requiredChecks ?? [];
  const blockedClaims = studioInterface.validationContract?.blockedClaims ?? [];
  const blockers = [
    ...(status === "blocked" ? ["studio interface status is blocked"] : []),
    ...((studioInterface.checks ?? []).flatMap((check) =>
      statusToAgentStatus(check.status) === "blocked"
        ? [`${readString(check.id)}: ${readString(check.expected)}`]
        : [],
    )),
  ];

  return {
    schemaVersion:
      AGENT_WORKSPACE_ONTOLOGY_GENERATED_APPLICATION_BRIDGE_SCHEMA_VERSION,
    status,
    appId: readString(artifact.appId),
    title: readString(artifact.title, "Ontology application"),
    generationMode: readString(artifact.generationMode),
    finalArtifactWriter: readString(sourceAuthority.finalArtifactWriter),
    promotionAuthority: readString(sourceAuthority.promotionAuthority),
    modelRoles: readModelRoles(sourceAuthority.modelRoles),
    deterministicCompilerHash: readString(
      sourceAuthority.deterministicCompilerHash,
      "not-recorded",
    ),
    screenCount: typeof artifact.screenCount === "number" ? artifact.screenCount : 0,
    componentCount:
      typeof artifact.componentCount === "number" ? artifact.componentCount : 0,
    actionFlowCount:
      typeof artifact.actionFlowCount === "number" ? artifact.actionFlowCount : 0,
    surfaceCount: studioSurfaces.length,
    flowStateCount: studioStates.length,
    actionCount: studioActions.length,
    screenshotCount: readStringArray(interfaceRefs.screenshots).length,
    requiredCheckCount: requiredChecks.length,
    passingCheckCount: countPassingChecks(studioInterface.checks),
    blockedClaims,
    previewRef: readString(interfaceRefs.previewHtml, "missing"),
    applicationJsonRef: readString(interfaceRefs.applicationJson, "missing"),
    generationProofRef: readString(interfaceRefs.generationProof, "missing"),
    sourcePromotionRef: readString(
      sourceAuthority.sourcePromotionRef ?? interfaceRefs.sourcePromotion,
      "missing",
    ),
    modelOrchestrationRef: readString(
      sourceAuthority.modelOrchestrationRef ?? interfaceRefs.modelOrchestration,
      "missing",
    ),
    mayPatchCogoJsonDirectly: false,
    publishBlocked: status !== "ready",
    blockers,
  };
}

export function buildAgentWorkspaceOntologyGeneratedApplicationBridgeFromProject(
  project: unknown,
): AgentWorkspaceOntologyGeneratedApplicationBridge | null {
  const studioInterface = readProjectOntologyGeneratedApplicationInterface(project);
  return studioInterface
    ? buildAgentWorkspaceOntologyGeneratedApplicationBridge(studioInterface)
    : null;
}
